import styled from "styled-components";
import {
  Button,
  Card,
  Divider,
  Flex,
  Input,
  Layout,
  Modal,
  Pagination,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import { COLORS } from "../../../utils/colors";
import {
  standalonePaginationCss,
  tablePaginationCss,
} from "../../components/tablePaginationStyles";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export const PageLayout = styled(Layout)`
  && {
    height: 100vh;
    background: linear-gradient(
      180deg,
      ${COLORS.pageGradientStart} 0%,
      ${COLORS.pageGradientEnd} 100%
    );
    font-family: "Roboto", sans-serif;
  }
`;

export const InnerLayout = styled(Layout)`
  && {
    background: transparent;
    height: 100vh;
    overflow-y: auto;
  }
`;

export const PageContent = styled(Content)`
  && {
    padding: 8px 28px 10px;
    position: relative;
    overflow-x: hidden;
  }

  &&::before {
    content: '';
    position: absolute;
    top: -160px;
    left: -160px;
    width: 230px;
    height: 230px;
    border-radius: 50%;
    background: rgba(33, 113, 204, 0.1);
    filter: blur(64px);
    pointer-events: none;
  }

  @media (max-width: 575px) {
    && {
      padding: 8px 16px 16px;
    }
  }
`;

export const StatsGrid = styled(Flex)`
  && {
    margin-bottom: 22px;
    padding: 16px 16px 5px;
    border-radius: 10px;
    border-top: 1px solid #ffffff;
    background: linear-gradient(
      180deg,
      rgba(228, 244, 255, 0.59) 0%,
      rgba(228, 244, 255, 0) 100%
    );
  }

  && > * {
    flex: 1 1 calc(20% - 12px);
    min-width: 0;
  }

  @media (max-width: 1199px) {
    && > * {
      flex: 1 1 calc(33.333% - 10px);
    }
  }

  @media (max-width: 767px) {
    && > * {
      flex: 1 1 calc(50% - 7px);
    }
  }
`;

export const StatCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 14px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 16px;
  }
`;

export const StatTopRow = styled(Flex)`
  && {
    margin-bottom: 12px;
  }
`;

export const StatIconCircle = styled(Flex)`
  && {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${COLORS.primary};
    color: ${COLORS.textOnPrimary};
  }

  && .anticon {
    font-size: 16px;
    color: ${COLORS.textOnPrimary};
  }
`;

export const StatLabel = styled(Text)`
  && {
    display: block;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: ${COLORS.textMuted};
    margin-bottom: 6px;
  }
`;

export const StatBottomRow = styled(Flex)``;

export const StatValue = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 28px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 32px !important;
  }
`;

export const RecentCallsCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 14px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 16px 18px;
  }
`;

export const RecentCallsHeader = styled(Flex)`
  && {
    margin-bottom: 14px;
  }

  @media (max-width: 991px) {
    && {
      flex-direction: column;
      align-items: stretch;
    }
  }
`;

export const RecentCallsTitleWrap = styled(Flex)``;

export const RecentCallsTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 16px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
  }
`;

export const RecentCallsSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const RecentCallsControls = styled(Flex)`
  @media (max-width: 991px) {
    && {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }
  }
`;

export const FilterScroll = styled(Flex)`
  @media (max-width: 991px) {
    && {
      max-width: 100%;
      overflow-x: auto;
      flex-wrap: nowrap;
      scrollbar-width: none;
      padding-bottom: 2px;
    }

    &&::-webkit-scrollbar {
      display: none;
    }

    && > * {
      flex: 0 0 auto;
    }
  }
`;

export const SearchInput = styled(Input)`
  && {
    height: 36px;
    border-radius: 10px;
    border: 1px solid ${COLORS.border};
    padding: 0 14px;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    width: 220px;
    background: ${COLORS.surface};

    .ant-input {
      font-size: 12px;
      background: transparent;
    }

    .ant-input-prefix {
      margin-inline-end: 10px;
    }

    .ant-input-prefix .anticon {
      color: ${COLORS.textSubtle};
      font-size: 14px;
    }

    &:hover,
    &:focus,
    &:focus-within {
      border-color: ${COLORS.primary};
      box-shadow: none;
    }
  }

  @media (max-width: 991px) {
    && {
      width: 100%;
    }
  }
`;

export const FilterButton = styled(Button)<{ $active?: boolean }>`
  && {
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    border: none;
    box-shadow: none;
    background: ${({ $active }) => ($active ? COLORS.primary : "transparent")};
    color: ${({ $active }) =>
      $active ? COLORS.textOnPrimary : COLORS.textBody};
    transition: none;

    &:hover,
    &:focus,
    &:active {
      background: ${({ $active }) =>
        $active ? COLORS.primary : "transparent"} !important;
      color: ${({ $active }) =>
        $active ? COLORS.textOnPrimary : COLORS.textBody} !important;
      box-shadow: none !important;
      border: none !important;
    }
  }
`;

export const ShowEntriesWrap = styled(Flex)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
    margin-left: 4px;
  }
`;

export const EntriesFooter = styled(Flex)`
  && {
    position: absolute;
    right: 0;
    bottom: 0;
    height: 32px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: ${COLORS.textBody};
  }
`;

export const ShowEntriesSelect = styled(Select)`
  && {
    width: 58px;
  }

  && .ant-select-selector {
    height: 28px !important;
    border-radius: 6px !important;
    padding: 0 8px 0 10px !important;
    border: none !important;
    background: ${COLORS.entriesbg} !important;
    box-shadow: none !important;
  }

  && .ant-select-selection-item {
    line-height: 26px !important;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: ${COLORS.primary};
    padding-inline-end: 14px !important;
  }

  && .ant-select-arrow {
    color: ${COLORS.primary};
    inset-inline-end: 8px;
    font-size: 10px;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    background: ${COLORS.entriesbg} !important;
    box-shadow: none !important;
  }
`;

export const DesktopTableWrap = styled.div`
  width: 100%;
  position: relative;

  && .ant-table-placeholder {
    min-height: 220px;
  }
`;

export const StyledTable = styled(Table)`
  && .ant-table {
    background: transparent;
    font-family: "Roboto", sans-serif;
  }

  && .ant-table-content > table,
  && .ant-table-body > table,
  && .ant-table-header > table {
    table-layout: fixed !important;
    width: 100% !important;
  }

  && .ant-table-header {
    background: transparent;
  }

  && .ant-table-thead > tr > th {
    background: transparent;
    color: ${COLORS.textMuted};
    font-size: 11px;
    font-weight: 600;
    text-transform: none;
    border-bottom: 1px solid ${COLORS.borderSoft};
    padding: 10px 16px;
  }

  && .ant-table-thead > tr > th::before {
    display: none !important;
  }

  && .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f1f3f6;
    padding: 12px 16px;
    font-size: 12px;
    color: ${COLORS.textBody};
    transition: background 120ms ease;
  }

  && .ant-table-tbody > tr:hover > td {
    background: ${COLORS.surfaceHover};
  }

  && .ant-table-body {
    scrollbar-width: thin;
    scrollbar-color: ${COLORS.border} transparent;
  }

  && .ant-table-body::-webkit-scrollbar {
    width: 6px;
  }

  && .ant-table-body::-webkit-scrollbar-track {
    background: transparent;
  }

  && .ant-table-body::-webkit-scrollbar-thumb {
    background: ${COLORS.border};
    border-radius: 999px;
  }

  && .ant-table-body::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.textSubtle};
  }

  ${tablePaginationCss}
`;

export const CallerCell = styled(Flex)`
  && {
    line-height: 1.3;
  }
`;

export const CallerName = styled(Text)`
  && {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    font-weight: 800;
    color: ${COLORS.textBody};
  }
`;

export const CallerPhone = styled(Text)`
  && {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const EllipsisCellText = styled(Text)`
  && {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const StatusPill = styled(Tag)<{ $success: boolean }>`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    padding: 3px 0;
    border-radius: 999px;
    margin: 0;
    border: none;
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
    background: ${({ $success }) =>
      $success ? COLORS.successBg : COLORS.unsuccessBg};
    color: ${({ $success }) =>
      $success ? COLORS.successText : COLORS.unsuccessText};
  }
`;

export const ActionCell = styled(Flex)``;

export const ViewButton = styled(Button)`
  && {
    height: 28px;
    padding: 0 18px;
    border-radius: 14px;
    background: ${COLORS.surface};
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    font-weight: 500;
    box-shadow: none;
    transition:
      background 120ms ease,
      color 120ms ease,
      border-color 120ms ease;

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
      box-shadow: none !important;
    }
  }
`;

export const CallList = styled(Flex)`
  && {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

export const CallPagination = styled(Pagination)`
  ${standalonePaginationCss}
`;

export const CallCard = styled(Card)`
  && {
    border: 1px solid ${COLORS.borderCard};
    border-radius: 12px;
    background: ${COLORS.surface};
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  }

  && .ant-card-body {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
`;

export const CallCardTop = styled(Flex)`
  && {
    margin-bottom: 2px;
  }
`;

export const CallId = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: ${COLORS.textMuted};
  }
`;

export const CallPurposeText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textBody};
  }
`;

export const CallCardBottom = styled(Flex)`
  && {
    margin-top: 2px;
    margin-bottom: 10px;
  }
`;

export const CallMetaText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const DateTimeCell = styled(Flex)`
  && {
    line-height: 1.3;
  }
`;

export const DateText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textBody};
  }
`;

export const TimeText = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    color: ${COLORS.textMuted};
  }
`;

export const MobileViewButton = styled(Button)`
  && {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    background: ${COLORS.surface};
    border: 1px solid ${COLORS.border};
    color: ${COLORS.textBody};
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    font-weight: 500;
    box-shadow: none;

    &:hover,
    &:focus {
      background: ${COLORS.surface} !important;
      color: ${COLORS.textBody} !important;
      border-color: ${COLORS.primary} !important;
      box-shadow: none !important;
    }
  }
`;

export const CallModal = styled(Modal)`
  && .ant-modal-content {
    padding: 0;
    border-radius: 20px;
    overflow: hidden;
  }
`;

export const CallModalHeader = styled(Flex)`
  && {
    padding: 22px 26px 18px;
  }
`;

export const CallModalHeaderIcon = styled(Flex)`
  && {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: ${COLORS.surface};
    color: #19b6a8;
    flex-shrink: 0;
  }

  && .anticon {
    font-size: 22px;
    color: #19b6a8;
  }
`;

export const CallModalTitle = styled(Title)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 19px !important;
    font-weight: 700 !important;
    color: ${COLORS.textBody} !important;
    margin: 0 !important;
    line-height: 24px !important;
  }
`;

export const CallModalSubtitle = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    color: ${COLORS.textMuted};
  }
`;

export const CallModalClose = styled(Button)`
  && {
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    color: ${COLORS.textMuted};
    flex-shrink: 0;

    &:hover,
    &:focus {
      background: ${COLORS.surfaceMuted} !important;
      color: ${COLORS.textBody} !important;
    }
  }

  && .anticon {
    font-size: 16px;
  }
`;

export const CallModalDivider = styled(Divider)`
  && {
    margin: 0;
    border-color: ${COLORS.borderSoft};
  }
`;

export const CallModalBody = styled(Flex)`
  && {
    padding: 24px 26px 26px;
  }
`;

export const CallDetailGrid = styled(Flex)`
  && {
    width: 100%;
  }

  && > * {
    flex: 1 1 calc(50% - 12px);
    min-width: 0;
  }

  @media (max-width: 575px) {
    && > * {
      flex: 1 1 100%;
    }
  }
`;

export const CallDetailField = styled(Flex)`
  && {
    min-width: 0;
  }
`;

export const CallDetailIconBox = styled(Flex)`
  && {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: ${COLORS.iconNotificationBg};
    color: ${COLORS.primary};
    flex-shrink: 0;
  }

  && .anticon {
    font-size: 17px;
    color: ${COLORS.primary};
  }
`;

export const CallDetailLabel = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${COLORS.textSubtle};
    line-height: 16px;
  }
`;

export const CallDetailValue = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: ${COLORS.textBody};
    line-height: 20px;
    word-break: break-word;
  }
`;

export const CallSummaryBox = styled(Flex)`
  && {
    width: 100%;
    border: 1px solid ${COLORS.borderCard};
    border-radius: 14px;
    padding: 18px 20px;
  }
`;

export const CallSummaryLabel = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${COLORS.textSubtle};
  }
`;

export const CallSummaryText = styled(Paragraph)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    color: ${COLORS.textBody};
    line-height: 22px;
    margin: 0 !important;
  }
`;

export const CallStatusRow = styled(Flex)`
  && {
    width: 100%;
  }
`;

export const CallStatusLabel = styled(Text)`
  && {
    font-family: "Roboto", sans-serif;
    font-size: 11px;
    font-weight: 500;
    color: ${COLORS.textMuted};
  }
`;
